import { LoopManager } from "./LoopManager.js";
import { Vector } from "./Vector.js";
import { Body } from "./Body.js";
import { KeyboardManager } from "./KeyboardManager.js";
import { EventId, EventManager, MyEventListener } from "./EventManager.js";

export class BodyManager implements MyEventListener {
    element: HTMLDivElement;
    controlBodies: Body[] = [];
    loopManager: LoopManager = new LoopManager();

    //プレイヤー操作関係
    player: Body | null = null;
    playerSetting: { [key: string]: number } = {
        moveSpeed: 5,
    };
    keyboardManager: KeyboardManager = new KeyboardManager();
    position: Vector = new Vector(0, 0);

    //カメラ関係
    cameraCenter: Body | null = null;
    maxCameraSpeed: number = Infinity;
    magnificationRate: number = 1;
    magnificationRateGoal: number = 1;

    //イベントリスナー
    readonly eventClassNames: string[] = ["gameLoop"];
    eventIds: EventId[] = [];
    addEvent(classNames: string[], handler: Function): EventId {
        const eventId = EventManager.addEvent({ classNames: classNames.filter((className) => this.eventClassNames.includes(className)), handler });
        this.eventIds.push(eventId);
        return eventId;
    }

    constructor() {
        this.element = document.createElement("div");
        this.element.classList.add("gamePanel");
        this.changeMagnificationRate(1);
        this.changeMagnificationRateContinuously(1);
        this.loopManager.addEvent(["loop"], () => {
            this.loop();
            EventManager.executeListeningEvents("gameLoop", this.eventIds);
        });
    }

    /**
     * 登録されているBodyの配列
     */
    get g$bodies(): Body[] {
        return this.controlBodies;
    }

    /**
     * 描画しているdiv要素
     */
    get g$element() {
        return this.element;
    }

    get g$isLooping() {
        return this.loopManager.g$isLooping;
    }

    get g$magnificationRateGoal() {
        return this.magnificationRateGoal;
    }

    /**
     * @param　プレイヤーとして登録するBody
     */
    set s$player(player: Body) {
        if (!this.controlBodies.includes(player)) {
            this.addBodies(player);
        }
        this.player = player;
    }

    /**
     * @param cameraCenter カメラの中心として設定するBody
     */
    set s$cameraCenter(cameraCenter: Body | null) {
        this.cameraCenter = cameraCenter;
    }

    /**
     * @param maxCameraSpeed カメラの移動速度の上限
     */
    set s$maxCameraSpeed(maxCameraSpeed: number) {
        this.maxCameraSpeed = maxCameraSpeed;
    }

    /**
     * 画面処理を開始する
     */
    start() {
        this.loopManager.start();
        this.keyboardManager.start();
        this.loop();
    }

    /**
     * 画面処理を停止する
     */
    stop() {
        this.loopManager.stop();
        this.keyboardManager.stop();
    }

    /**
     * 管理するBodyを追加する
     * @param bodies 追加するBody
     */
    addBodies(...bodies: Body[] | Body[][]) {
        if (bodies.length == 0) {
            throw new Error("長さ0の配列を指定しています");
        }
        const validBodies = Array.isArray(bodies[0]) ? bodies[0] : (bodies as Body[]);
        validBodies.forEach((body) => {
            if (!Array.from(this.element.children).includes(body.element)) {
                this.controlBodies.push(body);
                this.element.appendChild(body.element);
            }
        });
    }

    /**
     * ループ内容
     */
    protected loop(): void {
        //反発処理
        for (const body of this.controlBodies) {
            for (const body2 of this.controlBodies) {
                if (body != body2) {
                    body.repels(body2);
                }
            }
        }

        //プレイヤーを動かす
        this.movePlayer();

        //プレイヤーへのカメラの追従
        this.followCamera();

        //速度、加速度の処理
        this.controlBodies.forEach((body) => {
            body.progressTime();
        });

        //画面倍率変更
        if (this.magnificationRate != this.magnificationRateGoal) {
            this.changeMagnificationRate(this.magnificationRate + (this.magnificationRateGoal - this.magnificationRate) / 20);
        }
    }

    /**
     * キーボード入力に応じてプレイヤーを動かす
     */
    movePlayer(): void {
        if (this.player == null) {
            return;
        }
        const dash = this.keyboardManager.existsPressingKey(["ShiftLeft", "ShiftRight", "Shift"]) ? 1.75 : 1;
        switch (this.keyboardManager.getLatestPressingKey(["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "KeyD", "KeyA", "KeyS", "KeyW"])) {
            case "ArrowRight":
                this.player.move(new Vector(this.playerSetting.moveSpeed * dash, 0));
                break;
            case "KeyD":
                this.player.move(new Vector(this.playerSetting.moveSpeed * dash, 0));
                break;
            case "ArrowLeft":
                this.player.move(new Vector(-this.playerSetting.moveSpeed * dash, 0));
                break;
            case "KeyA":
                this.player.move(new Vector(-this.playerSetting.moveSpeed * dash, 0));
                break;
            case "ArrowDown":
                this.player.move(new Vector(0, this.playerSetting.moveSpeed * dash));
                break;
            case "KeyS":
                this.player.move(new Vector(0, this.playerSetting.moveSpeed * dash));
                break;
            case "ArrowUp":
                this.player.move(new Vector(0, -this.playerSetting.moveSpeed * dash));
                break;
            case "KeyW":
                this.player.move(new Vector(0, -this.playerSetting.moveSpeed * dash));
                break;
        }
    }

    /**
     * プレイヤーを中心とするようにカメラを追従させる
     */
    followCamera(): void {
        if (this.cameraCenter == null) {
            return;
        }
        const panelPosition = new Vector(+this.element.style.left.slice(0, -2), +this.element.style.top.slice(0, -2));
        const panelCenter = new Vector(this.element.clientWidth / 2, this.element.clientHeight / 2);
        const bodyCenter = this.cameraCenter.centerPosition.add(panelPosition);
        const differenceVector = panelCenter.sub(bodyCenter);
        if (differenceVector.isZero) {
            return;
        }
        const followVector = differenceVector.setSize(Math.min((differenceVector.size / 10) ** 2, differenceVector.size, this.maxCameraSpeed)).add(panelPosition);

        for (const body of this.controlBodies) {
            body.move(followVector);
        }
        this.position = this.position.add(followVector);
    }

    setCamera(): void {
        if (this.cameraCenter == null) {
            return;
        }
        const panelPosition = new Vector(+this.element.style.left.slice(0, -2), +this.element.style.top.slice(0, -2));
        const panelCenter = new Vector(this.element.clientWidth / 2, this.element.clientHeight / 2);
        const bodyCenter = this.cameraCenter.centerPosition.add(panelPosition);
        const differenceVector = panelCenter.sub(bodyCenter);
        if (differenceVector.isZero) {
            return;
        }
        for (const body of this.controlBodies) {
            body.move(differenceVector);
        }
        this.position = this.position.add(differenceVector);
    }

    /**
     * 画面倍率を変更する
     * @param rate 画面倍率
     */
    changeMagnificationRate(rate: number): void {
        this.magnificationRate = rate;
        // rate = (rate * document.getElementById("play")!.clientHeight) / 800;
        // console.log(document.getElementById("play")!.clientHeight / 800);
        this.element.style.width = rate * 100 + "%";
        this.element.style.height = rate * 100 + "%";
        this.element.style.transform = "scale(" + 1 / rate + ") translateY(" + Math.min(-(rate - 1) * 50, 0) + "%)";
    }

    /**
     * 連続的に画面倍率を変更する
     * @param magnificationRateGoal 画面倍率の目標値
     */
    changeMagnificationRateContinuously(magnificationRateGoal: number): void {
        this.magnificationRateGoal = magnificationRateGoal;
    }

    remove() {
        this.stop();
        EventManager.removeEvents(this.keyboardManager.eventIds);
        EventManager.removeEvents(this.loopManager.eventIds);
        EventManager.removeEvents(this.eventIds);
        this.element.remove();
    }
}
